/**
 * Member Classes Booking Page - Olympic Fit Wellness
 * Light theme matching member dashboard
 * Day/Week/Month views with calendar integration
 */
import { useState, useEffect, useCallback } from 'react';
import { format, addDays, subDays, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { 
  Calendar, 
  Clock, 
  User,
  Search,
  ChevronLeft,
  ChevronRight,
  Check,
  Users,
  Grid,
  List,
  CalendarDays
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';

// Session type badges
const SESSION_TYPES = {
  group_class: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Group Class' },
  one_on_one: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '1-on-1' },
  workshop: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Workshop' }
};

export default function ClassesBookingPage() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month'
  const [searchQuery, setSearchQuery] = useState('');
  const [sessions, setSessions] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bookingInProgress, setBookingInProgress] = useState(null);

  // Get date range based on view mode
  const getDateRange = useCallback(() => {
    switch (viewMode) {
      case 'week':
        return {
          start: startOfWeek(selectedDate, { weekStartsOn: 0 }),
          end: endOfWeek(selectedDate, { weekStartsOn: 0 })
        };
      case 'month':
        return {
          start: startOfMonth(selectedDate),
          end: endOfMonth(selectedDate)
        };
      default: // day
        return {
          start: startOfDay(selectedDate),
          end: endOfDay(selectedDate)
        };
    }
  }, [selectedDate, viewMode]);

  // Load sessions
  const loadData = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const { start, end } = getDateRange();
      
      const [sessionsData, bookingsData] = await Promise.all([
        api.getCalendarSessions({ start_date: start.toISOString(), end_date: end.toISOString() }),
        api.getMyCalendarBookings()
      ]);
      
      setSessions(sessionsData || []);
      
      const bookedIds = (bookingsData || [])
        .filter(b => ['confirmed', 'pending'].includes(b.status))
        .map(b => b.session_id || b.calendar_sessions?.id);
      setMyBookings(bookedIds);
      
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Failed to load classes');
    } finally {
      setIsLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter sessions
  const filteredSessions = sessions.filter(session => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      session.title?.toLowerCase().includes(query) ||
      session.staff_name?.toLowerCase().includes(query)
    );
  }).sort((a, b) => new Date(a.start) - new Date(b.start));

  // Group sessions by date for week/month view
  const sessionsByDate = filteredSessions.reduce((acc, session) => {
    const dateKey = format(new Date(session.start), 'yyyy-MM-dd');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(session);
    return acc;
  }, {});

  // Handle booking
  const handleBook = async (session) => {
    if (!user) {
      toast.error('Please log in to book a class');
      return;
    }

    setBookingInProgress(session.id);
    
    try {
      const result = await api.createCalendarBooking(session.id);
      
      if (result.success) {
        toast.success(result.message || 'Booked successfully!');
        setMyBookings(prev => [...prev, session.id]);
        loadData();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to book class');
    } finally {
      setBookingInProgress(null);
    }
  };

  // Navigation
  const navigate = (direction) => {
    const days = viewMode === 'month' ? 30 : viewMode === 'week' ? 7 : 1;
    setSelectedDate(prev => direction === 'next' ? addDays(prev, days) : subDays(prev, days));
  };

  const isBooked = (sessionId) => myBookings.includes(sessionId);
  const isFull = (session) => session.current_participants >= session.max_participants;

  // Get days for week/month view
  const getDaysInRange = () => {
    const { start, end } = getDateRange();
    return eachDayOfInterval({ start, end });
  };

  // Session Card Component
  const SessionCard = ({ session, compact = false }) => {
    const style = SESSION_TYPES[session.session_type] || SESSION_TYPES.group_class;
    const booked = isBooked(session.id);
    const full = isFull(session);
    const spotsLeft = session.max_participants - (session.current_participants || 0);

    return (
      <div 
        className={`bg-white border ${booked ? 'border-emerald-300' : 'border-neutral-200'} p-${compact ? '3' : '5'} hover:shadow-sm transition-shadow`}
        data-testid={`session-card-${session.id}`}
      >
        <div className={compact ? 'space-y-2' : 'flex flex-col md:flex-row md:items-center gap-4'}>
          {/* Time */}
          {!compact && (
            <div className="md:w-20 text-center md:text-left">
              <div className="text-xl font-semibold text-neutral-900">
                {format(new Date(session.start), 'h:mm')}
              </div>
              <div className="text-sm text-neutral-500 uppercase">
                {format(new Date(session.start), 'a')}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {compact && (
                <span className="text-sm text-neutral-500">
                  {format(new Date(session.start), 'h:mm a')}
                </span>
              )}
              <h3 className={`${compact ? 'text-sm' : 'text-lg'} font-medium text-neutral-900`}>
                {session.title}
              </h3>
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${style.bg} ${style.text}`}>
                {style.label}
              </span>
              {booked && (
                <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-100 text-emerald-700">
                  <Check className="w-3 h-3 inline mr-1" />
                  Booked
                </span>
              )}
            </div>
            
            <div className={`flex flex-wrap gap-3 ${compact ? 'text-xs' : 'text-sm'} text-neutral-500`}>
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {session.staff_name || 'Staff'}
              </span>
              {!compact && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(session.start), 'h:mm a')} - {format(new Date(session.end), 'h:mm a')}
                </span>
              )}
              <span className={`flex items-center gap-1 ${full ? 'text-red-500' : ''}`}>
                <Users className="w-3 h-3" />
                {spotsLeft} spots
              </span>
            </div>
          </div>

          {/* Book Button */}
          <div className={compact ? 'mt-2' : 'md:w-28'}>
            {booked ? (
              <button disabled className={`${compact ? 'w-full text-xs py-1.5' : 'w-full py-2'} bg-emerald-50 text-emerald-700 font-medium rounded border border-emerald-200`}>
                <Check className="w-3 h-3 inline mr-1" />
                Booked
              </button>
            ) : full ? (
              <button disabled className={`${compact ? 'w-full text-xs py-1.5' : 'w-full py-2'} bg-neutral-100 text-neutral-400 font-medium rounded`}>
                Full
              </button>
            ) : (
              <button
                onClick={() => handleBook(session)}
                disabled={bookingInProgress === session.id}
                className={`${compact ? 'w-full text-xs py-1.5' : 'w-full py-2'} bg-black hover:bg-neutral-800 text-white font-medium rounded transition-colors disabled:opacity-50`}
                data-testid={`book-btn-${session.id}`}
              >
                {bookingInProgress === session.id ? 'Booking...' : session.session_type === 'one_on_one' ? 'Request' : 'Book'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50 pt-24 pb-16" data-testid="classes-booking-page">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-normal tracking-[0.04em] uppercase text-neutral-900 mb-2">
            Book Classes
          </h1>
          <p className="text-neutral-500">Browse and book available sessions</p>
        </div>

        {/* Controls Row */}
        <div className="bg-white border border-neutral-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* View Mode Tabs */}
            <div className="flex border border-neutral-200 rounded overflow-hidden">
              {[
                { id: 'day', label: 'Day', icon: Calendar },
                { id: 'week', label: 'Week', icon: CalendarDays },
                { id: 'month', label: 'Month', icon: Grid }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setViewMode(id)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${
                    viewMode === id 
                      ? 'bg-black text-white' 
                      : 'bg-white text-neutral-600 hover:bg-neutral-50'
                  }`}
                  data-testid={`view-${id}`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Date Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('prev')}
                className="p-2 hover:bg-neutral-100 rounded transition-colors"
                data-testid="prev-btn"
              >
                <ChevronLeft className="w-5 h-5 text-neutral-600" />
              </button>
              
              <div className="min-w-[200px] text-center">
                <button
                  onClick={() => setSelectedDate(new Date())}
                  className="text-xs text-blue-600 hover:text-blue-700 mb-0.5 block mx-auto"
                >
                  Today
                </button>
                <h2 className="text-base font-medium text-neutral-900">
                  {viewMode === 'day' && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                  {viewMode === 'week' && `Week of ${format(getDateRange().start, 'MMM d')} - ${format(getDateRange().end, 'MMM d, yyyy')}`}
                  {viewMode === 'month' && format(selectedDate, 'MMMM yyyy')}
                </h2>
              </div>
              
              <button
                onClick={() => navigate('next')}
                className="p-2 hover:bg-neutral-100 rounded transition-colors"
                data-testid="next-btn"
              >
                <ChevronRight className="w-5 h-5 text-neutral-600" />
              </button>
            </div>

            {/* Search */}
            <div className="flex-1 md:max-w-xs ml-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search classes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-neutral-200 rounded text-sm focus:outline-none focus:border-neutral-400"
                  data-testid="search-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Loading */}
        {isLoading ? (
          <div className="bg-white border border-neutral-200 p-12 text-center">
            <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-neutral-500">Loading classes...</p>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="bg-white border border-neutral-200 p-12 text-center">
            <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 mb-2">No classes scheduled</h3>
            <p className="text-neutral-500 mb-4">
              {searchQuery 
                ? 'No classes match your search.'
                : `No classes scheduled for this ${viewMode}.`}
            </p>
            <button
              onClick={() => navigate('next')}
              className="px-4 py-2 bg-black text-white text-sm font-medium rounded hover:bg-neutral-800 transition-colors"
            >
              Check Next {viewMode === 'day' ? 'Day' : viewMode === 'week' ? 'Week' : 'Month'}
            </button>
          </div>
        ) : viewMode === 'day' ? (
          /* Day View - List */
          <div className="space-y-3">
            {filteredSessions.map(session => (
              <SessionCard key={session.id} session={session} />
            ))}
          </div>
        ) : (
          /* Week/Month View - Grid */
          <div className={`grid ${viewMode === 'week' ? 'grid-cols-7' : 'grid-cols-7'} gap-2`}>
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-neutral-500 uppercase py-2 bg-white border border-neutral-200">
                {day}
              </div>
            ))}
            
            {/* Day Cells */}
            {getDaysInRange().map(day => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const daySessions = sessionsByDate[dateKey] || [];
              const isToday = isSameDay(day, new Date());
              const isSelected = isSameDay(day, selectedDate);
              
              return (
                <div 
                  key={dateKey}
                  className={`bg-white border ${isToday ? 'border-blue-400' : 'border-neutral-200'} min-h-[120px] p-2`}
                >
                  <div 
                    className={`text-sm font-medium mb-2 ${isToday ? 'text-blue-600' : 'text-neutral-900'}`}
                  >
                    {format(day, 'd')}
                    {viewMode === 'month' && format(day, 'd') === '1' && (
                      <span className="text-xs text-neutral-400 ml-1">{format(day, 'MMM')}</span>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    {daySessions.slice(0, viewMode === 'week' ? 4 : 3).map(session => {
                      const style = SESSION_TYPES[session.session_type] || SESSION_TYPES.group_class;
                      const booked = isBooked(session.id);
                      
                      return (
                        <button
                          key={session.id}
                          onClick={() => {
                            setSelectedDate(day);
                            setViewMode('day');
                          }}
                          className={`w-full text-left text-xs p-1.5 rounded truncate ${
                            booked ? 'bg-emerald-100 text-emerald-700' : `${style.bg} ${style.text}`
                          } hover:opacity-80 transition-opacity`}
                          title={`${session.title} - ${format(new Date(session.start), 'h:mm a')}`}
                        >
                          {format(new Date(session.start), 'h:mm')} {session.title}
                        </button>
                      );
                    })}
                    {daySessions.length > (viewMode === 'week' ? 4 : 3) && (
                      <button
                        onClick={() => {
                          setSelectedDate(day);
                          setViewMode('day');
                        }}
                        className="w-full text-xs text-blue-600 hover:text-blue-700 text-left"
                      >
                        +{daySessions.length - (viewMode === 'week' ? 4 : 3)} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="mt-8 bg-white border border-neutral-200 p-4">
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-sm text-neutral-500">Session Types:</span>
            {Object.entries(SESSION_TYPES).map(([key, style]) => (
              <div key={key} className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded ${style.bg}`} />
                <span className="text-sm text-neutral-600">{style.label}</span>
                <span className="text-xs text-neutral-400">
                  {key === 'one_on_one' ? '(approval required)' : '(instant)'}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-emerald-100" />
              <span className="text-sm text-neutral-600">Your Booked</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
